import type { Prayer } from "./types";

export const prayer: Prayer = {
  slug: "prayer-for-self-control",
  title: "A prayer for self-control — when willpower has run out",
  h1: "A prayer for self-control",
  description:
    "A prayer for the moment you are about to do the thing again, four passages on restraint, and why self-control is listed as fruit rather than effort.",
  targets: [
    "prayer for self control",
    "prayer for discipline",
    "prayer for willpower",
    "prayer when tempted to give in",
  ],
  published: "2026-09-03",
  standfirst:
    "Self-control is the last item on Paul's list of fruit, and fruit is not something you produce by pushing harder.",
  takeaways: [
    "Pray it before the moment, not during. The prayer that works is the one said at four in the afternoon about nine in the evening.",
    "Galatians 5 calls self-control fruit of the Spirit, which is a claim about where it comes from — grown rather than manufactured, and slowly.",
    "Grace is not a reason to stop trying. Titus 2 says grace itself is what instructs us to live differently; the two are the same movement, not alternatives.",
  ],
  when: [
    "Before the hour you always lose, while you can still think clearly.",
    "Standing in front of the cupboard, the app, the tab, the message.",
    "After you have broken the same promise to yourself for the fourth week.",
    "When the plan was going well and you are about to decide that means you can stop keeping it.",
  ],
  prayer: [
    "God, I know exactly what I am about to do, and I have done it enough times to know how it ends. I am telling you before rather than after, which is new.",
    "I am not asking to be made a different person tonight. I am asking for the next twenty minutes, because that is usually how long the wanting lasts before it gets bored.",
    "Take the edge off it. And if you do not take the edge off it, give me something to do with my hands while it passes.",
    "You have not asked me to be strong. You have asked me to stay near you, and the strength is meant to be a by-product. Let me stop confusing the two.",
    "If I lose this one, do not let me use losing it as a reason to lose the rest of the week.",
  ],
  short: "God, give me the next twenty minutes. That is usually all it takes.",
  honest: [
    "This prayer does not remove the desire. Nothing in Scripture promises that, and a page that implies it sets up the reader to conclude, the next time the desire returns, that the prayer failed or they did.",
    "It is also not a substitute for changing the arrangement. Praying for self-control while the thing sits unlocked on your home screen is asking God to do the part you could do with a setting.",
    "And it is not a moral verdict on you. Compulsion has causes — sleep, loneliness, a job that is eating you — and the prayer that ignores all of them is only treating the last five seconds of a long chain.",
  ],
  verses: [
    {
      verse: {
        text: "But the fruit of the Spirit is love, joy, peace, patience, kindness, goodness, faith, gentleness, and self-control. Against such things there is no law.",
        ref: "Galatians 5:22–23",
      },
      note: "Self-control is at the end of a list of things described as fruit — grown, seasonal, the visible result of a plant being alive rather than a target hit by effort. The passage is contrasting this with law-keeping, which is the strategy most people bring to a habit they want to break. Note also that the whole list is singular fruit, not fruits: you do not get to pick self-control and leave patience.",
    },
    {
      verse: {
        text: "Like a city that is broken down and without walls is a man whose spirit is without restraint.",
        ref: "Proverbs 25:28",
      },
      note: "An image about defence, not about morality. A city without walls is not wicked; it is available — anything that wanders past can walk in and take what it likes. That is a far more accurate description of an evening with no boundary on it than any language about weakness.",
    },
    {
      verse: {
        text: "No temptation has taken you except what is common to man. God is faithful, who will not allow you to be tempted above what you are able, but will with the temptation also make the way of escape, that you may be able to endure it.",
        ref: "1 Corinthians 10:13",
      },
      note: "Two things people miss. First, the escape is provided with the temptation, which means it is usually already there and physical — a door, a different room, a phone left downstairs. Second, the promised outcome is endurance, not the removal of the pressure. It is a verse about surviving the wave, not about the sea going flat.",
    },
    {
      verse: {
        text: "For the grace of God has appeared, bringing salvation to all men, instructing us to the intent that, denying ungodliness and worldly lusts, we would live soberly, righteously, and godly in this present age;",
        ref: "Titus 2:11–12",
      },
      note: "The sentence that stops grace and discipline being opposites. Grace is the subject of the verb: it is grace that does the instructing, and what it instructs is a specific, sober, unglamorous way of living now. Anyone who has used grace as a reason to stop trying has been reading a shorter version of this verse.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "Pray it early, not in the doorway",
      id: "pray-it-early-not-in-the-doorway",
    },
    {
      type: "p",
      text: "By the time you are standing in front of the thing, the decision has largely been made by a version of you with fewer resources. Everything useful happens earlier: the prayer at four o'clock about nine o'clock, the arrangement made on Sunday about Wednesday.",
    },
    {
      type: "p",
      text: "That is not a failure of spirituality. It is how every serious tradition has ever talked about this — the desert fathers arranged their days precisely so the hard choice arrived as rarely as possible.",
    },
    {
      type: "h2",
      text: "The escape is usually a door",
      id: "the-escape-is-usually-a-door",
    },
    {
      type: "p",
      text: "1 Corinthians 10:13 promises a way out provided alongside the pressure, and in practice the way out is almost never a feeling. It is a room you can walk into, a friend you can text, a lock that takes ninety seconds to undo. {Screen time limits} fail for exactly this reason: a limit you can dismiss with one tap is not a wall.",
      links: {
        "Screen time limits": "/blog/screen-time-limits-dont-work",
      },
    },
    {
      type: "note",
      text: "This is the whole design of Bible Shield: the apps stay closed until you have prayed, so the gap between wanting and opening is filled with something rather than nothing. But any friction you cannot dismiss in one motion does the same job.",
    },
    {
      type: "h2",
      text: "Fruit takes a season",
      id: "fruit-takes-a-season",
    },
    {
      type: "p",
      text: "The most damaging expectation people bring to this prayer is that it should work tonight, and that failing tonight means the whole thing is fake. Fruit does not appear the week the tree is planted, and self-control is on that list rather than on a list of instructions for a reason.",
    },
    {
      type: "p",
      text: "So the honest measure is not whether you lost tonight. It is whether the losses are getting further apart, and whether the recovery after one is getting faster.",
    },
  ],
  faq: [
    {
      q: "What can I pray when I am about to give in?",
      a: '"God, give me the next twenty minutes." The urge is usually shorter than it feels, and asking for a survivable unit of time is a request you can actually watch being answered.',
    },
    {
      q: "Which Bible verse is about self-control?",
      a: "Galatians 5:22–23 lists it as fruit of the Spirit, and Proverbs 25:28 gives the memorable image — a person without restraint is a city with no walls, not wicked so much as undefended.",
    },
    {
      q: "Why does praying for self-control not seem to work?",
      a: "Often because it is being asked to do a job that belongs to an arrangement. If the thing is one tap away, the prayer is being asked to replace a wall. Change the setup, then pray about the part that is left.",
    },
  ],
  relatedPrayers: [
    "prayer-for-focus",
    "prayer-for-anxiety",
    "prayer-for-a-new-start",
  ],
  related: ["screen-time-limits-dont-work", "christian-app-blocker-guide"],
  relatedTopics: [
    "bible-verses-about-self-control",
    "bible-verses-about-temptation",
    "bible-verses-about-fasting",
  ],
  relatedPassages: ["philippians-4-13"],
};
